import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ChevronRight, DollarSign } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { COST_ARTICLE_LINKS } from "@/lib/costArticleLinks";

const PATH = "/learn/cost-guides";
const TITLE = "Colorado Marketing Cost Guides — Honest 2026 Pricing Hub";
const DESCRIPTION =
  "Every Colorado marketing cost guide in one place. Real pricing for web design, SEO, Google Maps, Google Ads, AI search, and industry-specific marketing — no fluff.";

const CANONICAL = `https://coloradowebimpressions.com${PATH}`;

type Guide = {
  path: string;
  title: string;
  blurb: string;
};

const SERVICE_GUIDES: Guide[] = [
  {
    path: "/learn/web-design-cost-colorado",
    title: "What should a business website cost in Colorado?",
    blurb: "Real price tiers from $500 templates to $30k custom builds.",
  },
  {
    path: "/learn/monthly-lead-generation-cost-colorado",
    title: "What does monthly lead generation cost in Colorado?",
    blurb: "Honest monthly budgets for SEO + ads + maps that actually drive leads.",
  },
  {
    path: "/learn/google-ads-management-cost-colorado",
    title: "Google Ads management cost in Colorado",
    blurb: "Management fees, ad spend, and what a real PPC budget looks like.",
  },
  {
    path: "/learn/google-maps-optimization-cost-colorado",
    title: "Google Maps / GBP optimization cost",
    blurb: "What it costs to rank in the Map Pack across Colorado cities.",
  },
  {
    path: "/learn/ai-geo-search-cost-colorado",
    title: "AI search & GEO cost in Colorado",
    blurb: "Getting cited by ChatGPT, Perplexity, and AI Overviews — real pricing.",
  },
  {
    path: "/learn/social-media-management-cost-colorado",
    title: "Social media management cost",
    blurb: "What organic and paid social really costs for Colorado small businesses.",
  },
  {
    path: "/learn/cheap-website-true-cost-colorado",
    title: "The true cost of a cheap website",
    blurb: "Why a $500 site usually costs you $5,000+ in missed jobs.",
  },
  {
    path: "/learn/diy-vs-hire-marketing-cost-colorado",
    title: "DIY vs. hiring a Colorado marketing agency",
    blurb: "Honest math on time, money, and opportunity cost.",
  },
  {
    path: "/learn/small-business-marketing-budget-colorado",
    title: "Small business marketing budget in Colorado",
    blurb: "How much should a Colorado small business actually spend on marketing?",
  },
];

const INDUSTRY_GUIDES: Guide[] = [
  { path: "/learn/hvac-marketing-cost-colorado", title: "HVAC marketing cost in Colorado", blurb: "Cost per lead, monthly budgets, and ROI benchmarks for HVAC." },
  { path: "/learn/roofing-marketing-cost-colorado", title: "Roofing marketing cost in Colorado", blurb: "Storm-chase vs. retail roofing budgets — real numbers." },
  { path: "/learn/plumber-marketing-cost-colorado", title: "Plumber marketing cost in Colorado", blurb: "Emergency-call CPL and monthly budgets for plumbing." },
  { path: "/learn/landscaper-marketing-cost-colorado", title: "Landscaper marketing cost in Colorado", blurb: "Seasonal budgets and lead costs for Colorado landscaping." },
  { path: "/learn/dental-marketing-cost-colorado", title: "Dental marketing cost in Colorado", blurb: "Patient-acquisition cost for Colorado dental practices." },
  { path: "/learn/law-firm-marketing-cost-colorado", title: "Law firm marketing cost in Colorado", blurb: "Case acquisition costs by practice area." },
  { path: "/learn/carpet-cleaning-marketing-cost-colorado", title: "Carpet cleaning marketing cost", blurb: "Per-job economics and ad budgets for carpet cleaners." },
  { path: "/learn/contractor-marketing-cost-colorado", title: "Contractor marketing cost in Colorado", blurb: "Remodeling and GC marketing budgets that work." },
];

// Sanity-check at module load: every guide listed here must also exist in the
// internal-link map, so visitors get a "Keep exploring" block on the article.
if (import.meta.env.DEV) {
  for (const g of [...SERVICE_GUIDES, ...INDUSTRY_GUIDES]) {
    if (!COST_ARTICLE_LINKS[g.path]) {
      // eslint-disable-next-line no-console
      console.warn(`[cost-guides hub] Missing link map entry for ${g.path}`);
    }
  }
}

const ITEM_LIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${CANONICAL}#collection`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  mainEntity: {
    "@type": "ItemList",
    itemListElement: [...SERVICE_GUIDES, ...INDUSTRY_GUIDES].map((g, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://coloradowebimpressions.com${g.path}`,
      name: g.title,
    })),
  },
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${CANONICAL}#breadcrumbs`,
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://coloradowebimpressions.com/" },
    { "@type": "ListItem", position: 2, name: "Learn", item: "https://coloradowebimpressions.com/learn" },
    { "@type": "ListItem", position: 3, name: "Cost Guides", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/learn/cost-guides")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(BREADCRUMB_SCHEMA) },
      { type: "application/ld+json", children: JSON.stringify(ITEM_LIST_SCHEMA) },
    ],
  }),
  component: CostGuidesHub,
});

function GuideCard({ g }: { g: Guide }) {
  return (
    <li>
      <a
        href={g.path}
        className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-spring hover:-translate-y-1 hover:border-brand-gold/60 hover:shadow-card-hover"
      >
        <h3 className="text-lg font-extrabold text-foreground group-hover:text-brand-gold-deep">
          {g.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
          {g.blurb}
        </p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-brand-gold-deep">
          See pricing
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </a>
    </li>
  );
}

function CostGuidesHub() {
  return (
    <SiteLayout>
      <header className="bg-gradient-dark py-16 text-brand-cream sm:py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs font-medium text-brand-cream/85">
            <Link to="/" className="hover:text-brand-gold">Home</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to="/learn" className="hover:text-brand-gold">Learn</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-brand-gold">Cost Guides</span>
          </nav>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-3 py-1 text-xs font-bold uppercase tracking-[0.15em] text-brand-gold">
            <DollarSign className="h-3 w-3" /> Colorado Pricing Hub
          </div>
          <h1 className="mt-4 text-3xl font-black leading-[1.1] text-white sm:text-4xl lg:text-5xl">
            Colorado marketing cost guides
          </h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-brand-cream/85">
            Every honest-pricing article we've published — by service and by
            industry. Real numbers, real ranges, no inflated agency math.
          </p>
        </div>
      </header>

      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
            Cost guides by service
          </h2>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">
            What different marketing services actually cost in Colorado in 2026.
          </p>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICE_GUIDES.map((g) => <GuideCard key={g.path} g={g} />)}
          </ul>
        </div>
      </section>

      <section className="bg-section-alt py-14 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
            Cost guides by industry
          </h2>
          <p className="mt-2 max-w-2xl text-base text-muted-foreground">
            Industry-specific pricing benchmarks, cost-per-lead numbers, and
            budget recommendations.
          </p>
          <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {INDUSTRY_GUIDES.map((g) => <GuideCard key={g.path} g={g} />)}
          </ul>
        </div>
      </section>

      <section className="bg-background py-14 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 text-center">
          <h2 className="text-2xl font-extrabold text-foreground sm:text-3xl">
            Skip the research — get a real Colorado quote
          </h2>
          <p className="mt-3 text-base text-muted-foreground">
            We'll audit your current setup and quote a scope specific to your
            city and industry. No long contracts. Owner-led from Colorado Springs.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/free-audit" className="inline-flex items-center gap-2 rounded-lg bg-brand-gold-deep px-5 py-3 text-sm font-bold text-background transition-smooth hover:bg-brand-gold">
              Get my free audit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-semibold text-foreground transition-smooth hover:border-brand-gold/60">
              See packages & pricing
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
